import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import { LoadingButton } from "@mui/lab";
import {
  IconButton,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIconTypeMap,
  Typography,
} from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import {
  useNavigateToNewBlankSearch,
  useNavigateToNewAutoSearch,
} from "hooks/navigation";
import { useNotifyStatus } from "contexts/status";
import { isPurchasingSearchType } from "src/utils/isPurchasingSearchType";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { PurchasingAutoRecipeMonthDialog } from "./PurchasingAutoRecipeMonthDialog";
import { useState } from "react";
import { useTenantTranslation } from "src/hooks/formatters";
import { SearchType } from "src/store/api/generatedApi";
import { AutoRecipeParametersDialog } from "./AutoRecipeParametersDialog";
import { searchTypeLabels } from "components/autoRecipeParameters/searchTypeLabels";

interface NewSearchOption {
  name: string;
  handleClick: () => void;
  icon: OverridableComponent<SvgIconTypeMap>;
  searchType?: SearchType;
}

const searchTypeIcons: Record<
  SearchType,
  OverridableComponent<SvgIconTypeMap>
> = {
  charging: TodayRoundedIcon,
  charging_two_charge: TodayRoundedIcon,
  charging_three_charge: TodayRoundedIcon,
  charging_fresh_anode: TodayRoundedIcon,
  charging_wet: TodayRoundedIcon,
  charging_two_charge_wet: TodayRoundedIcon,
  medium_term_charging: DateRangeRoundedIcon,
  purchasing: CalendarMonthRoundedIcon,
  purchasing_unrestricted: CalendarMonthRoundedIcon,
  simulator: TodayRoundedIcon,
};

interface SearchOptionsMenuProps {
  autoSearchTypes: SearchType[];
}

export const SearchOptionsMenu = ({
  autoSearchTypes,
}: SearchOptionsMenuProps) => {
  const { t } = useTenantTranslation();
  const [navigateToNewBlankSearch, { isLoading: isBlankSearchLoading }] =
    useNavigateToNewBlankSearch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [selectedSearchType, setSelectedSearchType] =
    useState<SearchType | null>(null);

  const [purchasingSearchType, setPurchasingSearchType] =
    useState<SearchType | null>(null);
  const [createAutoSearch, { isLoading: isAutoSearchLoading }] =
    useNavigateToNewAutoSearch();
  const notifyStatus = useNotifyStatus();
  const createSearch = (searchType: SearchType) =>
    createAutoSearch({
      auto_recipe_type: searchType,
    }).catch(() => {
      notifyStatus({ type: "error", text: t("unknownError") });
    });

  const options: NewSearchOption[] = [
    {
      name: "blankSearch",
      handleClick: () => {
        void navigateToNewBlankSearch();
      },
      icon: CalendarTodayRoundedIcon,
    },
    ...autoSearchTypes.map((searchType) => ({
      name: searchTypeLabels[searchType],
      handleClick: () => {
        if (isPurchasingSearchType(searchType))
          setPurchasingSearchType(searchType);
        else void createSearch(searchType);
      },
      icon: searchTypeIcons[searchType],
      searchType,
    })),
  ];

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <LoadingButton
        onClick={handleOpen}
        variant="contained"
        size="large"
        loading={isBlankSearchLoading || isAutoSearchLoading}
      >
        {t("newSearch")}
      </LoadingButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 0.5 }}
        slotProps={{ list: { disablePadding: true } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.name}
            onClick={() => {
              option.handleClick();
              handleClose();
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") {
                event.currentTarget.querySelector("button")?.focus();
                event.preventDefault();
              }
            }}
            sx={{ px: 2, py: 1 }}
          >
            <ListItemIcon>
              <option.icon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="button">{t(option.name)}</Typography>
            </ListItemText>
            {option.searchType !== undefined ? (
              <Tooltip title={t("autoAdjustParameters")}>
                <IconButton
                  size="small"
                  aria-label={`${t("autoAdjustParameters")}: ${t(option.name)}`}
                  sx={{ ml: 2 }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ")
                      event.stopPropagation();
                    if (
                      ["ArrowLeft", "ArrowUp", "ArrowDown"].includes(event.key)
                    ) {
                      event.currentTarget
                        .closest<HTMLElement>("[role=menuitem]")
                        ?.focus();
                      event.preventDefault();
                    }
                  }}
                  onKeyUp={(event) => {
                    if (event.key === "Enter" || event.key === " ")
                      event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedSearchType(option.searchType ?? null);
                    handleClose();
                  }}
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
      {purchasingSearchType !== null ? (
        <PurchasingAutoRecipeMonthDialog
          searchType={purchasingSearchType}
          close={() => {
            setPurchasingSearchType(null);
          }}
        />
      ) : null}
      {selectedSearchType !== null ? (
        <AutoRecipeParametersDialog
          key={selectedSearchType}
          searchType={selectedSearchType}
          close={() => {
            setSelectedSearchType(null);
          }}
        />
      ) : null}
    </>
  );
};
